/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { JSONGeneratorAPI } from "@/features/utils/api/json-generator";
import type { MetadataField } from "@/features/documents/types";
import { Sparkle } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { toast } from "sonner";

interface GenerateFieldsDialogProps {
  onFieldsGenerated: (fields: MetadataField[]) => void;
}

export function GenerateFieldsDialog({
  onFieldsGenerated,
}: GenerateFieldsDialogProps) {
  const { t } = useTranslation("documents");
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t("metadata.generate.description_required"));
      return;
    }

    setIsGenerating(true);
    try {
      const result = await JSONGeneratorAPI.generate<{
        fields: MetadataField[];
      }>({
        prompt,
        json_id: "rag_metadata_fields",
      });

      // Ensure all fields have required property set to false by default
      const fieldsWithDefaults = result.fields.map((field) => ({
        ...field,
        required: field.required ?? false,
      }));

      onFieldsGenerated(fieldsWithDefaults);
      setOpen(false);
      setPrompt("");
      toast.success(
        t("metadata.generate.generated", { count: fieldsWithDefaults.length }),
      );
    } catch (error: any) {
      toast.error(error.message || t("metadata.generate.failed"));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Sparkle className="h-4 w-4 mr-2" weight="fill" />
          {t("metadata.generate.trigger")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("metadata.generate.title")}</DialogTitle>
          <DialogDescription>
            {t("metadata.generate.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="prompt">
              {t("metadata.generate.prompt_label")}
            </Label>
            <Textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t("metadata.generate.prompt_placeholder")}
              rows={5}
            />
            <p className="text-xs text-muted-foreground">
              {t("metadata.generate.prompt_hint")}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("metadata.generate.cancel")}
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating}>
            {isGenerating
              ? t("metadata.generate.generating")
              : t("metadata.generate.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
